/**
 * A single shared deadline helper (ED-18).
 *
 * Lives in its OWN module on purpose: the flows that need it (the editor's baked-layer download,
 * the wizard's asset sync and submit) sit either side of `bakedSync.js`, which several suites
 * replace wholesale with `vi.mock`. Putting the helper there would make an unrelated timeout
 * disappear inside those mocks.
 */

/**
 * ED-18 — race `p` against a deadline so a wedged network call can NEVER spin forever.
 *
 * Three flows had no timeout at all (the editor's baked-layer download, the wizard's asset sync,
 * the wizard's submit): a hung R2/API connection left the button on "Syncing…"/"Saving…" with no
 * recourse but restarting the app. Rejects with a NAMED error (`label` + the budget) so the
 * caller's existing failure handling reports something actionable. The underlying promise is not
 * cancellable — it is simply no longer awaited — so callers must treat a timeout as "unknown
 * outcome", exactly like any other network failure. Its rejection is swallowed here so a late
 * failure can't surface as an unhandled rejection.
 */
export function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const guarded = p.catch((e) => { throw e; });
  void guarded.catch(() => { /* a post-timeout rejection must not go unhandled */ });
  return Promise.race([
    guarded,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)), ms);
    }),
  ]).finally(() => { if (timer) clearTimeout(timer); });
}

/**
 * Default deadlines for the two kinds of raw `fetch` this app does outside `JsonClient`
 * (which has always had its own 180s cap). Presigned PUTs carry whole images, so they get
 * the longer budget; GETs of an already-baked asset are small and should fail fast.
 *
 * `UPLOAD_TIMEOUT_MS` is a FLOOR, not the whole budget — see `sizeAwareBudget`.
 */
export const UPLOAD_TIMEOUT_MS = 120_000;
export const DOWNLOAD_TIMEOUT_MS = 60_000;

/**
 * The saved-matte restore is a SHORTER deadline than a general download, because the editor
 * is wrong while it runs: the canvas shows "not removed" for a template whose background WAS
 * removed, and the next Save would persist that. 60 s of a silently wrong editor is a demo
 * lost; 20 s is long enough for a real CDN fetch of a matte PNG and short enough that the
 * retry banner arrives while the operator is still looking at the screen.
 */
export const MATTE_RESTORE_TIMEOUT_MS = 20_000;

/**
 * Slowest uplink we are willing to call "wedged" rather than "slow": 40 KB/s ≈ 320 kbps.
 * Below this an upload is not making meaningful progress on any connection worth waiting for.
 */
const MIN_UPLOAD_BYTES_PER_SEC = 40 * 1024;

/** Byte length of a request body, when it is one we can measure. */
function bodyBytes(body: BodyInit | null | undefined): number {
  if (!body) return 0;
  if (typeof body === "string") return body.length;
  if (body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (ArrayBuffer.isView(body)) return body.byteLength;
  return 0; // FormData / stream — not measurable here, fall back to the floor
}

/**
 * A flat wall-clock deadline is wrong for uploads, because it cannot tell a WEDGED socket
 * from a SLOW one. The pickers accept 32 MB, and 32 MB inside a flat 120s demands a
 * sustained ~273 KB/s uplink — so a perfectly healthy upload on a typical residential or
 * mobile connection would be aborted by the very guard meant to rescue it. Worse,
 * `uploadBakedAssets` PUTs several full-resolution PNGs through `Promise.all`, so they
 * share one uplink while each runs its own timer.
 *
 * Scale the budget by payload size instead, with the flat value as a floor: a 32 MB body
 * gets ~13 minutes, a 200 KB logo still gets the 120s floor, and a stalled socket of any
 * size still dies. Bodies we cannot measure keep the floor.
 */
export function sizeAwareBudget(body: BodyInit | null | undefined, floorMs = UPLOAD_TIMEOUT_MS): number {
  const bytes = bodyBytes(body);
  if (!bytes) return floorMs;
  return Math.max(floorMs, Math.ceil((bytes / MIN_UPLOAD_BYTES_PER_SEC) * 1000));
}

/** The asset host answers an `<img>` request (no `Origin`) with no `Vary: Origin` and no CORS
 *  header, and the browser may serve that cached copy to a later `fetch`, which fails CORS. */
export const NO_HTTP_CACHE: Readonly<RequestInit> = { cache: "no-store" };

/**
 * `fetch` with a hard deadline that actually ABORTS the request.
 *
 * Every direct-to-storage call in the app — the presigned PUTs (template case photo, baked
 * layers, brand logo, appearance + image-library + option-choice uploads) and the GETs that
 * read a baked asset back — went out as a bare `fetch` with no timeout at all. A wedged
 * connection (captive portal, half-open socket, an R2 edge that accepts and then stalls)
 * left them pending forever, which is what puts a button on "Uploading…" until the app is
 * restarted. `withTimeout` above would unblock the caller but leave the socket held, so
 * this aborts as well: use it for `fetch`, `withTimeout` for anything else.
 *
 * The timer is raced explicitly, not just wired to the signal, because a non-cooperative
 * fetch implementation (and several test doubles) ignore `AbortSignal` entirely.
 */
export async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  ms: number = UPLOAD_TIMEOUT_MS,
  label = "Request",
): Promise<Response> {
  // The caller's `ms` is the FLOOR for a body-carrying request, not the ceiling: a big
  // upload on a slow-but-working link must not be killed (see `sizeAwareBudget`). Bodyless
  // GETs are unaffected — `bodyBytes` returns 0 and the floor stands.
  const budget = init.body ? sizeAwareBudget(init.body, ms) : ms;
  const ctl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      ctl.abort();
      reject(new Error(`${label} timed out after ${Math.round(budget / 1000)}s`));
    }, budget);
  });
  try {
    // `init.signal` wins if the caller already passes one (e.g. a user-cancellable download);
    // ours still fires the rejection, so the deadline holds either way.
    const started = fetch(url, { ...init, signal: init.signal ?? ctl.signal });
    void started.catch(() => { /* a post-timeout rejection must not go unhandled */ });
    return await Promise.race([started, deadline]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * WS-ERR F-5 — ONE budget for the headers, the body AND the decode.
 *
 * `fetchWithTimeout` clears its timer the moment the `Response` resolves, i.e. when the HEADERS
 * arrive. Everything after that — `arrayBuffer()`, and whatever the caller does with the bytes —
 * ran with no deadline at all. A server that answers `200 OK` and then stalls mid-body (a
 * half-open socket, a proxy that died after the headers, a CDN edge that hangs) therefore
 * defeated the deadline completely: the editor's matte restore waited for ever, which is the
 * exact hang `MATTE_RESTORE_TIMEOUT_MS` was added to bound, arriving through the one door the
 * bound did not cover.
 *
 * This keeps the timer running until the bytes (and, when the caller supplies a `decode`, the
 * decoded value) are in hand, and ABORTS the request when it expires rather than merely
 * ceasing to wait — so the socket is released instead of leaking for the life of the tab.
 *
 * `fetchWithTimeout` is unchanged and still correct for its callers: they want the RESPONSE
 * (to read `res.ok`, to stream, or to discard), and widening its contract would change the
 * meaning of every existing deadline in the app.
 */
/**
 * One signal that aborts when EITHER input does. `AbortSignal.any` is the platform answer;
 * the fallback exists because the desktop WebView's floor predates it.
 */
function combineSignals(a: AbortSignal | null | undefined, b: AbortSignal): AbortSignal {
  if (!a) return b;
  const any = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof any === "function") return any([a, b]);
  const ctl = new AbortController();
  const stop = (reason?: unknown) => ctl.abort(reason);
  if (a.aborted) stop(a.reason);
  else a.addEventListener("abort", () => stop(a.reason), { once: true });
  if (b.aborted) stop(b.reason);
  else b.addEventListener("abort", () => stop(b.reason), { once: true });
  return ctl.signal;
}

export async function fetchBytesWithTimeout<T = Uint8Array>(
  url: string,
  init: RequestInit = {},
  ms: number = DOWNLOAD_TIMEOUT_MS,
  label = "Request",
  opts?: {
    /**
     * Runs inside the same budget: once it expires this stops AWAITING the decode and rejects.
     * A decode is not cancellable, so a wedged one keeps running to completion in the
     * background — the caller is simply no longer blocked on it. (The fetch IS aborted; that
     * part is real cancellation.)
     */
    decode?: (bytes: Uint8Array) => Promise<T>;
  },
): Promise<T> {
  const ctl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let expired = false;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      expired = true;
      ctl.abort();
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
  });
  const work = (async (): Promise<T> => {
    // The caller's signal AND ours. Passing only `init.signal` (the old `??`) meant a caller
    // who supplied one — a user-cancellable download — silently lost the deadline's abort, so
    // the request it was meant to kill ran on. `AbortSignal.any` where the runtime has it, a
    // manual bridge where it does not (older WebViews).
    const signal = combineSignals(init.signal, ctl.signal);
    const res = await fetch(url, { ...init, signal });
    if (!res.ok) throw new Error(`${label} failed — HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    // An abort that lands between the two awaits surfaces here rather than as a confusing
    // "0 bytes" success.
    if (expired) throw new Error(`${label} timed out after ${Math.round(ms / 1000)}s`);
    const bytes = new Uint8Array(buf);
    return opts?.decode ? await opts.decode(bytes) : (bytes as unknown as T);
  })();
  void work.catch(() => { /* a post-timeout rejection must not go unhandled */ });
  try {
    return await Promise.race([work, deadline]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
